import React, { useState } from 'react'
import SEO from '../components/SEO'

export default function SaelgDinBaadPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    adresse: '',
    postnummer: '',
    by: '',
    baad_maerke: '',
    baadtype: 'Motorbåd',
    pris: '',
    beskrivelse: '',
    website_url: '', // Antispam Honeypot
  })

  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files)
    if (selected.length > 5) {
      setErrorMessage('Du kan højst uploade 5 billeder/filer på én gang.')
      return
    }
    const totalSize = selected.reduce((acc, f) => acc + f.size, 0)
    if (totalSize > 20 * 1024 * 1024) {
      setErrorMessage('Den samlede filstørrelse må højst være 20 MB.')
      return
    }
    setErrorMessage('')
    setFiles(selected)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')

    if (!formData.navn.trim() || !formData.telefon.trim() || !formData.email.trim()) {
      setErrorMessage('Udfyld venligst navn, telefonnummer og e-mailadresse.')
      return
    }

    setSubmitting(true)

    try {
      const data = new FormData()
      data.append('form_type', 'Sælg din båd')
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key])
      })

      files.forEach((file) => {
        data.append('billeder', file)
      })

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data,
      })

      if (response.ok) {
        setSubmitted(true)
      } else {
        setErrorMessage(
          'Der opstod en fejl ved indsendelsen. Prøv venligst igen eller ring direkte på tlf. 40 20 70 34.'
        )
      }
    } catch (err) {
      setErrorMessage('Der opstod en netværksfejl. Prøv venligst igen eller ring på 40 20 70 34.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-10 sm:py-16">
      <SEO
        title="Sælg din båd - Nordfyn Marine"
        description="Sælg din båd trygt og effektivt gennem Nordfyn Marine. Solgt eller gratis mæglerkoncept."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Header Banner */}
        <div className="bg-[#071320] text-white p-8 sm:p-12 border border-slate-800 shadow-xl relative overflow-hidden rounded-2xl">
          <div className="relative z-10 max-w-3xl space-y-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#2BABE3]">
              Professionel Bådformidling
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light tracking-wide text-white">
              Sælg din båd gennem Nordfyn Marine
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed max-w-2xl">
              Nordfyn Marine tilbyder professionel og tryg bådformidling i hele Danmark efter princippet{' '}
              <strong className="text-[#2BABE3] font-semibold">Solgt eller gratis</strong>. Vi klarer al
              markedsføring, fremvisning, købsaftale og juridisk papirarbejde for dig.
            </p>
          </div>
          {/* Subtle background overlay image */}
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=70,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/smuk-luksus-motorbaad-hero.png"
            alt="Bådsalg"
            className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-luminosity pointer-events-none"
          />
        </div>

        {/* Benefits Bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 border border-slate-200 shadow-sm space-y-2 rounded-2xl">
            <div className="w-10 h-10 bg-[#1E7BC1]/10 text-[#1E7BC1] flex items-center justify-center text-lg font-bold rounded-xl">
              <i className="fa-solid fa-handshake" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900 uppercase tracking-wider text-xs">
              Solgt eller gratis
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Bliver din båd ikke solgt, koster det dig absolut intet. Ingen oprettelsesgebyrer eller skjulte salgsomkostninger.
            </p>
          </div>

          <div className="bg-white p-6 border border-slate-200 shadow-sm space-y-2 rounded-2xl">
            <div className="w-10 h-10 bg-[#1E7BC1]/10 text-[#1E7BC1] flex items-center justify-center text-lg font-bold rounded-xl">
              <i className="fa-solid fa-bullhorn" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900 uppercase tracking-wider text-xs">
              Bred Markedsføring
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Din båd præsenteres professionelt med billeder og specifikationer på alle relevante danske og europæiske bådportaler.
            </p>
          </div>

          <div className="bg-white p-6 border border-slate-200 shadow-sm space-y-2 rounded-2xl md:col-span-2 lg:col-span-1">
            <div className="w-10 h-10 bg-[#1E7BC1]/10 text-[#1E7BC1] flex items-center justify-center text-lg font-bold rounded-xl">
              <i className="fa-solid fa-file-contract" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900 uppercase tracking-wider text-xs">
              Tryg Kontrakthåndtering
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Vi udarbejder juridisk bindende købsaftaler, håndterer deponering af købesum og overdragelse sikkert for begge parter.
            </p>
          </div>
        </div>

        {/* Main 2-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left Column: Submission Form */}
          <div className="lg:col-span-8 space-y-8">
            {submitted ? (
              <div className="bg-white p-8 sm:p-12 border border-emerald-200 text-center space-y-5 shadow-sm rounded-2xl">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                  <i className="fa-solid fa-check" />
                </div>
                <h2 className="font-serif font-bold text-2xl sm:text-3xl text-slate-900">
                  Mange tak! Din båd er sendt til Nordfyn Marine.
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                  Vi har modtaget dine oplysninger og vil gennemgå dem snarest muligt. Nordfyn Marine tager kontakt til dig for en uforpligtende snak om vurderingen af din båd.
                </p>
                <div className="pt-4">
                  <a
                    href="tel:40207034"
                    className="inline-flex items-center gap-2 bg-[#071320] text-white px-8 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#1E7BC1] transition-colors whitespace-nowrap rounded-xl"
                  >
                    <i className="fa-solid fa-phone text-[#2BABE3]" />
                    <span className="whitespace-nowrap">Ring direkte: 40 20 70 34</span>
                  </a>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white p-6 sm:p-10 border border-slate-200 shadow-sm space-y-8 rounded-2xl"
              >
                {/* Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                {errorMessage && (
                  <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-3 rounded-xl">
                    <i className="fa-solid fa-circle-exclamation text-base shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Sælgers kontaktoplysninger */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="font-serif font-bold text-base sm:text-lg text-slate-900 uppercase tracking-wider">
                      1. Sælgers kontaktoplysninger
                    </h3>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Trin 1 af 2
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Fulde navn"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Telefonnummer <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="Tlf. nummer"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        E-mailadresse <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="E-mail"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Adresse
                      </label>
                      <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="Vejnavn og husnr."
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Postnummer
                      </label>
                      <input
                        type="text"
                        name="postnummer"
                        value={formData.postnummer}
                        onChange={handleChange}
                        placeholder="Postnr."
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        By
                      </label>
                      <input
                        type="text"
                        name="by"
                        value={formData.by}
                        onChange={handleChange}
                        placeholder="By"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>
                  </div>
                </div>

                {/* Bådens oplysninger */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="font-serif font-bold text-base sm:text-lg text-slate-900 uppercase tracking-wider">
                      2. Bådens oplysninger
                    </h3>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                      Trin 2 af 2
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Bådens mærke og model
                      </label>
                      <input
                        type="text"
                        name="baad_maerke"
                        value={formData.baad_maerke}
                        onChange={handleChange}
                        placeholder="f.eks. Nimbus 280 Coupé"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Bådtype
                      </label>
                      <select
                        name="baadtype"
                        value={formData.baadtype}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      >
                        <option value="Motorbåd">Motorbåd</option>
                        <option value="Motorsejler">Motorsejler</option>
                        <option value="Sejlbåd">Sejlbåd</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Forventet pris (DKK)
                      </label>
                      <input
                        type="text"
                        name="pris"
                        value={formData.pris}
                        onChange={handleChange}
                        placeholder="f.eks. 250.000"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Beskrivelse og stand
                    </label>
                    <textarea
                      name="beskrivelse"
                      rows="4"
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      placeholder="Fortæl kort om båden, motor, årgang, udstyr og generel stand..."
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1E7BC1]"
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="pt-2 space-y-1.5">
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                      Upload billeder af båden (Valgfrit - Maks. 5 filer, op til 20 MB)
                    </label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      className="block w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-5 file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-[#071320] file:text-white hover:file:bg-[#1E7BC1] file:cursor-pointer transition-colors file:rounded-xl"
                    />
                    {files.length > 0 && (
                      <p className="text-xs text-emerald-600 font-medium pt-1">
                        {files.length} fil(er) valgt til upload.
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-10 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all shadow-md flex items-center justify-center gap-2 rounded-xl"
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" />
                        <span>Indsender oplysninger...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        <span>Indsend oplysninger om båd</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right Column: Sidebar with Images & Contact Cards */}
          <div className="lg:col-span-4 space-y-8">
            {/* Visual Image Card 1 */}
            <div className="bg-white border border-slate-200 overflow-hidden shadow-sm relative group rounded-2xl">
              <div className="h-64 relative overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/moderne-luksus-motorbaad-kategori.png"
                  alt="Sælg din båd trygt"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/90 via-[#071320]/30 to-transparent" />
                <div className="absolute top-4 right-4 bg-[#2BABE3] text-[#071320] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 shadow-md rounded-full">
                  Solgt eller gratis
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white space-y-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#2BABE3] font-semibold">
                    Nordfyn Marine Formidling
                  </span>
                  <h4 className="font-serif font-light text-xl leading-tight">
                    Vi skaffer den rette køber til din båd
                  </h4>
                </div>
              </div>
              <div className="p-5 space-y-3 bg-white">
                <p className="text-xs text-slate-600 leading-relaxed">
                  Med mere end 14 års erfaring og et stærkt netværk i Danmark og Europa sikrer vi dig et hurtigt og professionelt salg.
                </p>
              </div>
            </div>

            {/* Direct Contact Card */}
            <div className="bg-[#071320] text-white p-6 border border-slate-800 space-y-4 shadow-md rounded-2xl">
              <div className="space-y-1 border-b border-slate-800 pb-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#2BABE3] font-semibold">
                  Personlig Rådgivning
                </span>
                <h4 className="font-serif font-light text-xl text-white">
                  Spørgsmål til salg af din båd?
                </h4>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Du er altid velkommen til at ringe til Nordfyn Marine for en uforpligtende drøftelse af din båds markedsværdi og salgsmuligheder.
              </p>

              <div className="space-y-2.5 pt-2">
                <a
                  href="tel:40207034"
                  className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-slate-700 transition-colors group rounded-xl"
                >
                  <div className="w-8 h-8 bg-[#1E7BC1] text-white flex items-center justify-center shrink-0 rounded-lg">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400">
                      Ring direkte (hele døgnet)
                    </span>
                    <span className="text-sm font-bold text-white group-hover:text-[#2BABE3] transition-colors whitespace-nowrap">
                      40 20 70 34
                    </span>
                  </div>
                </a>

                <a
                  href="mailto:info@nordfyn-marine.dk"
                  className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-slate-700 transition-colors group rounded-xl"
                >
                  <div className="w-8 h-8 bg-[#1E7BC1] text-white flex items-center justify-center shrink-0 rounded-lg">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-widest text-slate-400">
                      Send en e-mail
                    </span>
                    <span className="text-sm font-bold text-white group-hover:text-[#2BABE3] transition-colors whitespace-nowrap">
                      info@nordfyn-marine.dk
                    </span>
                  </div>
                </a>
              </div>
            </div>

            {/* Visual Image Card 2 */}
            <div className="bg-white border border-slate-200 overflow-hidden shadow-sm relative group rounded-2xl">
              <div className="h-56 relative overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/sejlbaad.jpg"
                  alt="Sejlbåd og motorbåd formidling"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/80 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2BABE3]">
                    Hele Danmark
                  </span>
                  <h5 className="font-serif text-lg font-light">
                    Mangeårig erfaring med alle bådtyper
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
